import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api from "services/api";

/**
 * Shared status/config state for the Server screen (2026-10-21).
 *
 * WHY A CONTEXT
 * Every tab used to fetch /server/status and /server/config on its own, so
 * opening Stats + Setup made four requests for two payloads, and the health
 * banner could only ever appear on the Stats tab. "Refresh now" also lived
 * inside the Stats tab, which meant you could not force a collection while
 * looking at History or Logs — the one moment you most want to.
 *
 * Now the page fetches once, polls while it is open, and every tab reads the
 * same snapshot. The header owns the refresh action.
 */

const ServerDataContext = createContext(null);

// The collector writes every 60s; polling at the same cadence keeps the
// "collected Xs ago" line honest without hammering the API.
const POLL_INTERVAL_MS = 60000;

export const ServerDataProvider = ({ children }) => {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  // Bumped after every successful load so tabs can re-derive from a fresh
  // "now" (relative ages otherwise freeze between renders).
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    try {
      const [statusResponse, configResponse] = await Promise.all([
        api.get("/server/status"),
        api.get("/server/config"),
      ]);

      setStatus(statusResponse.data || null);
      setConfig(configResponse.data || null);
      setError("");
      setVersion((current) => current + 1);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          requestError?.response?.data?.detail ||
          "Failed to load server status."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post("/server/refresh");
      await load();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.response?.data?.error ||
          "Failed to collect server metrics."
      );
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  const value = useMemo(() => {
    const snapshot = status?.snapshot || null;
    const issues = Array.isArray(status?.issues) ? status.issues : [];
    const counts = status?.counts || { critical: 0, warning: 0 };

    return {
      status,
      config,
      snapshot,
      staleness: status?.staleness || {},
      issues,
      counts,
      healthStatus: status?.healthStatus || "unknown",
      driveTrend: status?.driveTrend || null,
      loading,
      refreshing,
      error,
      version,
      refresh,
      reload: load,
    };
  }, [status, config, loading, refreshing, error, version, refresh, load]);

  return (
    <ServerDataContext.Provider value={value}>
      {children}
    </ServerDataContext.Provider>
  );
};

export const useServerData = () => {
  const context = useContext(ServerDataContext);
  if (!context) {
    throw new Error("useServerData must be used inside <ServerDataProvider>");
  }
  return context;
};

export default ServerDataProvider;
